import { FormBlock, FormCard } from '@qbe/ads-blocks-forms';
import { Button, Dropdown, Form, FormGroupLabel, TextField, Typography } from '@qbe/ads-core';
import React from 'react';
import { Controller } from 'react-hook-form';

import { LOCAL_STORAGE_EMAIL_KEY } from '~/constants/app';
import { useAppContent, useAppDispatch } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { Group, updateGroups, updateUserEmail } from '~/slices/appDetails';
import { qalFormCompleteAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

const channelOptions = [
  {
    id: 'directSmallBusiness',
    description: 'Direct Small Business',
  },
  {
    id: 'costco',
    description: 'Costco',
  },
  {
    id: 'thriday',
    description: 'Thriday',
  },
  {
    id: 'velocity',
    description: 'Velocity',
  },
];

type StaffProps = {
  defaultEmail?: string;
};

type StaffFormValues = {
  email: string;
  channel: Group;
};

export const Staff: React.FC<StaffProps> = ({ defaultEmail }) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.staffPortal',
  });
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  const { handleSubmit, control, formState } = useFormWithAnalytics<StaffFormValues>({
    mode: 'onChange',
    defaultValues: {
      email: defaultEmail,
      channel: 'directSmallBusiness',
    },
  });

  const onSubmit = handleSubmit((data: StaffFormValues | undefined) => {
    if (!data || !data.email || !data.channel) {
      console.error('Form data is incomplete');
      return;
    }

    const { email, channel } = data;

    localStorage.setItem(LOCAL_STORAGE_EMAIL_KEY, email);
    handleFormComplete();
    dispatch(updateGroups(['staff', channel]));
    dispatch(updateUserEmail(email));
    dispatch(
      navigateAction({
        to: '/',
        navigate,
        searchParams: {
          group: ['staff', channel].filter((g) => g !== 'directSmallBusiness'),
        },
      })
    );
  });

  return (
    <>
      <FormCard
        title={getTextContent('title')}
        variant="pageTitle"
        description={getTextContent('description')}
      />

      <FormCard variant="content" data-testid="staff-email">
        <Form onSubmit={onSubmit} noValidate>
          <FormGroupLabel
            title={getTextContent('fields.emailInput.title')}
            alignCenter
            titleSuffix={
              <ContextualHelpWithButton tracking="help: Enter your QBE staff email address">
                <Typography>{getJSXContent('contextualhelpers.emailInput.description')}</Typography>
              </ContextualHelpWithButton>
            }
          />
          <FormBlock preset="input" maxWidth="100%">
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  id="email"
                  type="email"
                  label={getTextContent('fields.emailInput.label')}
                  {...{
                    'data-track-value': 'mask',
                    'data-track': 'Enter your QBE staff email address',
                  }}
                  error={fieldState.invalid}
                  helperText={
                    fieldState.invalid ? getTextContent('fields.emailInput.invalidText') : ''
                  }
                  required
                  InputLabelProps={{ required: false }}
                  displayIcon
                  defaultValue={String(defaultEmail)}
                />
              )}
              name="email"
              rules={{
                required: true,
                pattern: /^[\w-.]+@qbe.com/i,
              }}
            />
          </FormBlock>

          <FormGroupLabel title={getTextContent('fields.referralChannelInput.title')} alignCenter />
          <FormBlock preset="input" maxWidth="100%">
            <Controller
              control={control}
              render={({ field }) => (
                <Dropdown
                  optionList={channelOptions.map((o) => ({
                    label: o.description,
                    value: o.id,
                    'data-track': 'Referral Partner Select',
                    'data-track-value': 'capture',
                  }))}
                  label=""
                  id="channel"
                  {...field}
                />
              )}
              name="channel"
            />
          </FormBlock>

          <FormBlock preset="button">
            <Button
              variant="primary-on-light"
              label={getTextContent('buttons.formSubmit')}
              type="submit"
              disabled={!formState.isValid}
              data-track="Submit - pay your bill now"
            />
          </FormBlock>
        </Form>
      </FormCard>
    </>
  );
};

const StaffView = () => {
  // Note: rewritten due to the rules of hooks lint rule
  const email = localStorage.getItem(LOCAL_STORAGE_EMAIL_KEY);

  return <Staff defaultEmail={email || ''} />;
};

export default StaffView;
